
import 'pdfjs-dist/build/pdf.worker.entry';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import { adminTheme } from 'theme/adminTheme';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionIcon from '@mui/icons-material/Description';

interface PdfProps {
  stepExecutionId?: number;
  onSuccessGeneration: (data: any) => void;
}

export default function Pdf({ stepExecutionId, onSuccessGeneration }: PdfProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if the PDF can be loaded by making a HEAD request
    const checkPdfAvailability = async () => {
      try {
        const response = await fetch(`/api/v1/filestorage/download/execution/${stepExecutionId}`, {
          method: 'HEAD',
        });

        if (!response.ok) {
          if (response.status === 403) {
            setError('Neturite teisės peržiūrėti šio dokumento');
          } else if (response.status === 404) {
            setError('Dokumentas nerastas');
          } else {
            setError('Įvyko klaida kraunant dokumentą');
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking PDF availability:', err);
      }
    };

    if (stepExecutionId) {
      checkPdfAvailability();
    }
  }, [stepExecutionId]);

  const handleIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const iframe = event.currentTarget;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

      // Check if iframe loaded an error page (text content instead of PDF)
      if (iframeDoc) {
        const bodyText = iframeDoc.body?.textContent || '';
        if (bodyText.includes('cantBeExecuted') || bodyText.includes('error') || bodyText.includes('exception')) {
          setError('Dokumentas dar nesugeneruotas arba neturite teisės jo peržiūrėti');
          setLoading(false);
          return;
        }
      }

      onSuccessGeneration(true);
      setLoading(false);
    } catch (err) {
      // If we can't access iframe content (cross-origin), assume PDF loaded successfully
      console.log('PDF loaded (cross-origin)');
      onSuccessGeneration(true);
      setLoading(false);
    }
  };

  const handleIframeError = () => {
    setError('Nepavyko įkelti dokumento');
    setLoading(false);
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 600,
            bgcolor: adminTheme.colors.background,
            border: `1px solid ${adminTheme.colors.error}40`,
            borderRadius: adminTheme.borderRadius.xl,
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${adminTheme.colors.error}20 0%, ${adminTheme.colors.error}10 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 64, color: adminTheme.colors.error }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary, mb: 2 }}>
            Dokumento įkėlimo klaida
          </Typography>
          <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 5,
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${adminTheme.colors.primary}20 0%, ${adminTheme.colors.primary}10 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: adminTheme.colors.primary }} />
          </Box>
          <CircularProgress size={48} sx={{ color: adminTheme.colors.primary, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
            Dokumentas kraunasi...
          </Typography>
          <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mt: 1 }}>
            Prašome palaukti...
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          marginBottom: '1rem',
          height: '100%',
          display: loading ? 'none' : 'block',
        }}
      >
        <iframe
          src={`/api/v1/filestorage/download/execution/${stepExecutionId}`}
          width="100%"
          height="100%"
          title="PDF Document"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          style={{ border: 'none' }}
        >
          <p>Jūsų naršyklė nepalaiko PDF peržiūros.</p>
        </iframe>
      </Box>
    </>
  );
}
